import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { View } from 'react-native';

import { Button, VStack, theme } from '@squire/ui';

import type { BottomSheetRef } from '@/components/_shared/BottomSheet';
import { BottomSheet } from '@/components/_shared/BottomSheet';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';

import { ScreenHeader } from '../ScreenHeader';
import { LanguageOptionItem } from './LanguageOptionItem';
import { LANGUAGE_OPTIONS } from './languageOptions';
import { styles } from './styles';
import type { LanguageBottomSheetProps, LanguageBottomSheetRef } from './types';
import { useLanguageBottomSheet } from './useLanguageBottomSheet';

const LanguageBottomSheet = forwardRef<
  LanguageBottomSheetRef,
  LanguageBottomSheetProps
>(({ onDismiss, testID = 'language-bottom-sheet' }, ref) => {
  const { t } = useScreenTranslations('LanguageBottomSheet');
  const bottomSheetRef = useRef<BottomSheetRef>(null);

  const {
    selectedLanguage,
    isLanguageChanged,
    isLoading,
    shouldClose,
    getLanguageNameInCurrentLanguage,
    handleLanguageSelect,
    handleConfirm,
    handleDismiss,
    syncWithI18n,
    resetShouldClose,
  } = useLanguageBottomSheet({ onDismiss });

  const isConfirmDisabled = !isLanguageChanged || isLoading;

  // Auto-close bottom sheet when fetching completes after language change
  useEffect(() => {
    if (shouldClose) {
      bottomSheetRef.current?.close();
      resetShouldClose();
    }
  }, [shouldClose, resetShouldClose]);

  useImperativeHandle(ref, () => ({
    open: () => {
      syncWithI18n();
      bottomSheetRef.current?.open();
    },
    close: () => {
      bottomSheetRef.current?.close();
    },
  }));

  return (
    <BottomSheet
      accessibilityLabel={t('title')}
      ref={bottomSheetRef}
      testID={testID}
      scrollable={false}
      onDismiss={handleDismiss}
      enableDynamicSizing
      style={styles.bottomSheet}
    >
      <VStack space={theme.spacing[4]}>
        <ScreenHeader title={t('title')} isLoading={isLoading} />

        <VStack space={12}>
          <View style={styles.languageContainer}>
            {LANGUAGE_OPTIONS.map((option) => (
              <LanguageOptionItem
                key={option.value}
                value={option.value}
                nameKey={option.nameKey}
                isSelected={selectedLanguage === option.value}
                disabled={isLoading}
                onSelect={handleLanguageSelect}
                getLanguageNameInCurrentLanguage={
                  getLanguageNameInCurrentLanguage
                }
                testID={`${testID}-option-${option.value}`}
              />
            ))}
          </View>

          <Button
            onPress={handleConfirm}
            disabled={isConfirmDisabled}
            variant="primary"
            size="medium"
            style={styles.confirmButton}
            accessibilityRole="button"
            accessibilityLabel={t('accessibility.confirmButton')}
            accessibilityHint={t('accessibility.confirmButtonHint')}
            aria-disabled={isConfirmDisabled || undefined}
          >
            {t('confirmButton')}
          </Button>
        </VStack>
      </VStack>
    </BottomSheet>
  );
});

LanguageBottomSheet.displayName = 'LanguageBottomSheet';
export { LanguageBottomSheet };
